import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Input, Modal, Select } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { QUESTION_TYPE, QUESTION_TYPE_META, QUESTION_TYPE_OPTIONS, RATING_SCALE_META } from "pages/SurveyManagement/constants";
import { APP_LANGUAGES } from "util/locale";
import TopicSelect from "./TopicSelect";
import styles from "./style.module.scss";

const { TextArea } = Input;
const EMPTY = { type: QUESTION_TYPE.RATING, text: "", ratingScale: "1-5", options: [], topic: "" };

// Adds or edits one question bank question: its wording, type, scale or options, topic and
// translations. Editing a question surveys use rewrites them all; past answers keep theirs.
const BankQuestionModal = ({ open, item, topics, primaryLanguage, saving, onCancel, onSave }) => {
  const { t } = useTranslation();
  const [question, setQuestion] = useState(EMPTY);
  const [translations, setTranslations] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setQuestion(item ? { ...EMPTY, ...item.question } : EMPTY);
    setTranslations(item?.translations || {});
    setError("");
  }, [open, item]);

  const meta = QUESTION_TYPE_META[question.type] || {};
  const patch = (changes) => {
    setQuestion((prev) => ({ ...prev, ...changes }));
    setError("");
  };
  const setOption = (i, value) => patch({ options: question.options.map((o, j) => (j === i ? value : o)) });
  const patchTranslation = (lang, changes) =>
    setTranslations((prev) => ({ ...prev, [lang]: { ...(prev[lang] || {}), ...changes } }));
  const usedIn = item?.used_in || [];
  const languages = APP_LANGUAGES.filter((l) => l.code !== primaryLanguage);

  const submit = () => {
    const options = (question.options || []).map((o) => (o || "").trim()).filter(Boolean);
    if (!question.text.trim()) {
      setError(t("surveySettings.bank.error_text"));
      return;
    }
    if (meta.hasOptions && options.length < 2) {
      setError(t("surveySettings.bank.error_options"));
      return;
    }
    const out = { type: question.type, text: question.text.trim() };
    if (meta.hasScale) out.ratingScale = question.ratingScale || "1-5";
    if (meta.hasOptions) out.options = options;
    if ((question.topic || "").trim()) out.topic = question.topic.trim();
    ["answerLength", "urlDomain", "videoMinSeconds", "videoMaxSeconds", "videoInstructions"].forEach((key) => {
      if (question[key] !== undefined && question[key] !== "") out[key] = question[key];
    });
    const cleanTranslations = {};
    Object.entries(translations).forEach(([lang, local]) => {
      const text = (local?.text || "").trim();
      const localOptions = (local?.options || []).map((o) => (o || "").trim());
      if (text) cleanTranslations[lang] = { text, ...(meta.hasOptions ? { options: localOptions } : {}) };
    });
    onSave(out, cleanTranslations);
  };

  return (
    <Modal
      open={open}
      title={t(item ? "surveySettings.bank.edit_title" : "surveySettings.bank.add_title")}
      okText={t("surveySettings.knowledge.save")}
      cancelText={t("surveySettings.knowledge.cancel")}
      confirmLoading={saving}
      onOk={submit}
      onCancel={onCancel}
      destroyOnClose
      width={680}
    >
      {usedIn.length ? (
        <Alert
          type="warning"
          showIcon
          className={styles.bankAlert}
          message={t("surveySettings.bank.used_warning", { count: usedIn.length })}
          description={usedIn.map((s) => s.title).join(", ")}
        />
      ) : null}
      <div className={styles.modalField}>
        <label>{t("surveySettings.bank.type")}</label>
        <Select
          className={styles.fullWidth}
          value={question.type}
          onChange={(type) =>
            patch({
              type,
              options: QUESTION_TYPE_META[type]?.hasOptions && !question.options?.length ? ["", ""] : question.options,
            })
          }
          options={QUESTION_TYPE_OPTIONS.filter((o) => o.value !== QUESTION_TYPE.VIDEO).map((o) => ({
            value: o.value,
            label: t(o.labelKey),
          }))}
        />
      </div>
      <div className={styles.modalField}>
        <label>{t("surveySettings.bank.text")}</label>
        <TextArea
          value={question.text}
          onChange={(e) => patch({ text: e.target.value })}
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={1000}
        />
      </div>
      {meta.hasScale ? (
        <div className={styles.modalField}>
          <label>{t("surveySettings.bank.scale")}</label>
          <Select
            className={styles.fullWidth}
            value={question.ratingScale || "1-5"}
            onChange={(ratingScale) => patch({ ratingScale })}
            options={Object.entries(RATING_SCALE_META).map(([value, m]) => ({ value, label: t(m.labelKey) }))}
          />
        </div>
      ) : null}
      {meta.hasOptions ? (
        <div className={styles.modalField}>
          <label>{t("surveySettings.bank.options")}</label>
          {(question.options || []).map((opt, i) => (
            <div key={i} className={styles.optionRow}>
              <Input value={opt} onChange={(e) => setOption(i, e.target.value)} maxLength={200} />
              <Button
                type="text"
                icon={<CloseOutlined />}
                disabled={question.options.length <= 2}
                onClick={() => patch({ options: question.options.filter((_, j) => j !== i) })}
              />
            </div>
          ))}
          <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => patch({ options: [...question.options, ""] })}>
            {t("surveyManagement.add_option")}
          </Button>
        </div>
      ) : null}
      <div className={styles.modalField}>
        <label>{t("surveySettings.bank.topic")}</label>
        <TopicSelect
          value={question.topic || ""}
          onChange={(topic) => patch({ topic })}
          topics={topics}
          allowClear
          className={styles.fullWidth}
        />
        <p className={styles.intro}>{t("surveySettings.bank.topic_hint")}</p>
      </div>
      <div className={styles.modalField}>
        <label>{t("surveySettings.bank.translations")}</label>
        <p className={styles.intro}>{t("surveySettings.bank.translations_hint")}</p>
        {languages.map((lang) => (
          <div key={lang.code} className={styles.translationRow}>
            <span className={styles.translationLang}>{lang.nativeLabel}</span>
            <div className={styles.translationFields}>
              <Input
                value={translations[lang.code]?.text || ""}
                onChange={(e) => patchTranslation(lang.code, { text: e.target.value })}
                maxLength={1000}
              />
              {meta.hasOptions && (translations[lang.code]?.text || "").trim()
                ? (question.options || []).map((opt, i) => (
                    <Input
                      key={i}
                      size="small"
                      placeholder={opt}
                      value={translations[lang.code]?.options?.[i] || ""}
                      onChange={(e) => {
                        const next = [...(translations[lang.code]?.options || [])];
                        next[i] = e.target.value;
                        patchTranslation(lang.code, { options: next });
                      }}
                    />
                  ))
                : null}
            </div>
          </div>
        ))}
      </div>
      {error ? <p className={styles.formError}>{error}</p> : null}
    </Modal>
  );
};

export default BankQuestionModal;
